import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import Container from '@/components/public/ui/Container';
import TimelineReferences, { type Reference } from '@/components/public/ui/TimelineReferences';
import { parishName, useSite } from '@/hooks/useSite';

type Entry = {
  id: number;
  era: string | null;
  title: string;
  description: string | null;
  references: Reference[];
};

// Chronological timeline of history blocks (ano · título · descrição ·
// referências), configured by the parish in the onboarding wizard.
export default function History({ entries }: { entries: Entry[] }) {
  const site = useSite();

  return (
    <>
      <Seo title="História" description={`História e património de ${parishName(site)}.`} />
      <PageHero badge="A nossa terra" title="História." accentWord="História." />
      <Container className="py-8">
        {entries.length === 0 ? (
          <p className="text-on-surface-light mx-auto max-w-3xl text-center">
            Este conteúdo estará disponível em breve.
          </p>
        ) : (
          <div className="flex flex-col py-4">
            {entries.map((entry, i) => {
              const isLast = i === entries.length - 1;
              return (
                <div key={entry.id} className="flex gap-5">
                  {/* Left rail: node + connector line */}
                  <div className="flex w-3 flex-none flex-col items-center">
                    <span className="ring-background bg-primary mt-1.5 h-3 w-3 flex-none rounded-full ring-4" />
                    {!isLast && <span className="bg-line mt-1 w-px flex-1" />}
                  </div>
                  <div className={`flex-1 ${isLast ? '' : 'pb-10'}`}>
                    {entry.era && (
                      <span className="text-secondary text-xs font-bold tracking-[0.08em] uppercase">
                        {entry.era}
                      </span>
                    )}
                    <h3 className="font-headline text-on-surface mt-1.5 text-2xl font-semibold">
                      {entry.title}
                    </h3>
                    {entry.description && (
                      <p className="text-on-surface-light mt-2 leading-relaxed">
                        {entry.description}
                      </p>
                    )}
                    {entry.references.length > 0 && (
                      <TimelineReferences references={entry.references} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Container>
    </>
  );
}
